import { useCallback, useState, useEffect } from "react";
import { StrategiesDropdown } from "../StrategiesDropdowns/StrategiesDropdown";
import { OptionsDropdown } from "../StrategiesDropdowns/OptionsDropdown";
import { useOptionsInstruments } from "../../hooks/api/mm/useOptionsInstruments";
import InstrumentChart from "../OptionsTrades/InstrumentChart";
import { useMMBybitAssets } from "../../utils/asset";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import { FilterWrapper, TradesWrapper, ChartWrapper } from "./style";
import Checkbox from "../shared/Checkbox";
import { CheckboxContainer } from "../shared/Checkbox/style";
import StrategiesChart from "./StrategiesChart";
import { useMarkets } from "../../hooks/api/aevo/useMarkets";
import DatetimePicker from "../StrategiesDropdowns/DatetimePicker";
import { MarketDropdown } from "../Dropdown/MarketDropdown";

// Helper function to get date range for past N days
const getPastDateRange = (days: number): [Date, Date] => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return [start, end];
};

export function MMStrategies() {
  const [underlyingAsset, setAsset] = useState<string>();
  const [strategy, setStrategy] = useState<string>("PERP MM");
  const [selectedAsset, setSelectedAsset] = useState<string>("ETH");
  const [fetchParams, setFetchParams] = useState<{
    strategy?: string;

  }>({});
  const [fetchKey, setFetchKey] = useState<number>(0);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(getPastDateRange(2));
  const [showAllTrades, setShowAllTrades] = useState(false);

  const handleFetch = useCallback(() => {
    setFetchParams({ strategy });
    setFetchKey((prevKey) => prevKey + 1);
  }, [strategy]);

  const handleDateChange = (startTimestamp: number, endTimestamp: number) => {
    const startDate = new Date(startTimestamp * 1000);
    const endDate = new Date(endTimestamp * 1000);
    setDateRange([startDate, endDate]);
  };

  // Automatically fetch data on component mount with default values
  useEffect(() => {
    handleFetch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: fetchedData } = useOptionsInstruments(
    underlyingAsset ?? "",
    dateRange[0] ? String(Math.floor(dateRange[0].getTime() / 1000)) : "0",
    dateRange[1] ? String(Math.floor(dateRange[1].getTime() / 1000)) : String(Math.floor(Date.now() / 1000))
  );

  const data = underlyingAsset && dateRange[0] && dateRange[1] ? fetchedData ?? [] : [];

  const filteredData = data?.filter((item) => item.InstrumentName.slice(0, 3) === underlyingAsset?.slice(0, 3));
  const uniqueInstrumentNames = Array.from(new Set(filteredData?.map((item) => item.InstrumentName)));

  const markets = useMarkets();

  let assets;
  if (strategy === "PRELAUNCH") {
    assets = markets.data
    ?.filter((market) => market.pre_launch)
    .map((market) => market.underlying_asset) || [];
  } else {
    assets = strategy === "VERTEX" ? ["ETH", "BTC", "SOL"] : markets.data?.map((market) => market.underlying_asset);
  }

  // Filter by selected asset if one is selected
  if (selectedAsset && assets) {
    assets = assets.filter((asset) => asset === selectedAsset);
  }

  const { data: bybitAssets } = useMMBybitAssets();
  return (
    <TradesWrapper>
      <FilterWrapper>
        <DatetimePicker
          onDateChange={(startTimestamp, endTimestamp) => handleDateChange(startTimestamp, endTimestamp)}
          overridePlaceholder="Select Date Range"
          initialDateRange={dateRange}
        />
        <StrategiesDropdown onClick={setStrategy} overridePlaceholder="Select Strategy" value={strategy} />
        {strategy === "OPTIONS" && <OptionsDropdown onClick={setAsset} overridePlaceholder="Select Asset" />}
        {strategy !== "OPTIONS" && <MarketDropdown onClick={setSelectedAsset} overridePlaceholder="Select Asset" value={selectedAsset} />}
        {strategy === "PRELAUNCH" && <CheckboxContainer>
          <Checkbox
            checked={showAllTrades}
            onToggle={() => setShowAllTrades(!showAllTrades)}
            title="All Trades"
          />
        </CheckboxContainer>}
        <Button
          buttonTheme={ButtonThemeEnum.NEUTRAL2}
          disabled={!strategy}
          onClick={handleFetch}
          style={{ minHeight: 24, maxHeight: 24 }}
        >
          Fetch
        </Button>
      </FilterWrapper>
      {fetchParams.strategy && strategy !== "OPTIONS" && fetchKey > 0 && assets?.map((asset) => (
        <ChartWrapper key={`${fetchKey}-${asset}`}>
          <StrategiesChart
            key={`${fetchKey}-${asset}`}
            instrumentName={asset}
            startTime={dateRange[0] ? dateRange[0].getTime() / 1000 : 0}
            endTime={dateRange[1] ? dateRange[1].getTime() / 1000 : 0}
            strategy={strategy as string}
            bybitAssets={bybitAssets}
            showAllTrades={showAllTrades}
          /></ChartWrapper>
      ))}

      {fetchParams.strategy && underlyingAsset && strategy === "OPTIONS" && fetchKey > 0 && uniqueInstrumentNames.map((instrumentName) => (
        <ChartWrapper key={`${fetchKey}-${underlyingAsset}-${instrumentName}`}>
          <InstrumentChart
            key={`${fetchKey}-${underlyingAsset}-${instrumentName}`}
            instrumentName={instrumentName}
            startTime={dateRange[0] ? dateRange[0].getTime() / 1000 : 0}
            endTime={dateRange[1] ? dateRange[1].getTime() / 1000 : 0}
          />
        </ChartWrapper>
      ))}
    </TradesWrapper>
  );
}
